import {sendAnalyticsEvent} from '#/analytics/analytics';
import {LoadingSpinner} from '#/atoms/loading-spinner';
import AsyncButton from '#/components/async-button/component';
import {IconButton} from '#/components/buttons/icon-button';
import {WishlistButton} from '#/components/buttons/wishlist-button';
import {DotPercentageCollateralPositionIndicator} from '#/components/indicators/dot-percentage-collateral-position-indicator';
import {LandMarketIndicator} from '#/components/indicators/market-indicator';
import {LandThumbnail} from '#/components/lands/land-thumbnail';
import {useLandRentalYield} from '#/hooks/query/use-land-rental-yield';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {useRefSrollIntoView} from '#/hooks/use-ref-scroll-into-view';
import {RentTab} from '#/navigation/tabs';
import {ClassName} from '#/utils/utils';
import {ChevronDownIcon, InformationCircleIcon} from '@heroicons/react/24/solid';
import {
  Land,
  LandAccountInfo,
  LandMarketInfo,
  TransactionPortfolioValue,
  getLandPremium,
  liquidityReceivedForLiquidityProviderToken,
} from '@shareland/shareland-common';
import {FunctionComponent, ReactNode, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useToggle} from 'react-use';

export const PortfolioLandAsset: FunctionComponent<
  {
    land: Land;
    portfolioValue: TransactionPortfolioValue;
    landMarketInfo: LandMarketInfo;
    landAccountInfo: LandAccountInfo;
    isCurrentUser: boolean;
    selected: boolean;
  } & ClassName
> = ({className, land, portfolioValue, landMarketInfo, landAccountInfo, isCurrentUser, selected}) => {
  const {t} = useTranslation('translation');

  const displayPref = useCoinDisplayPreferences();
  const {
    actionMenu: {openTrade, openRent, openInformation},
  } = useQueryNavigationActions();

  const [divRef, divRefScrollIntoView] = useRefSrollIntoView<HTMLDivElement>(100);

  const [isOpen, toggleIsOpen] = useToggle(false);

  const premiumRatio = t('utils.percentageFixed', {
    value: getLandPremium(landMarketInfo.marketPrice, landMarketInfo.oraclePrice),
  });

  const onInformationClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    openInformation(land);
  };
  const onTradeButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    openTrade(land);
  };
  const onRentButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    openRent(land, RentTab.RENT);
  };
  const onExpandButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    toggleIsOpen();
    sendAnalyticsEvent.propertyAsset({land, landMarketInfo, landAccountInfo, premiumRatio});
  };
  const onClaimButtonClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    openRent(land, RentTab.CLAIM);
  };

  useEffect(() => {
    if (selected) {
      divRefScrollIntoView();
    }
  }, [selected, divRefScrollIntoView]);

  return (
    <div
      className={`w-full pt-5 bg-graybluedark rounded-2xl border data-[selected=true]:border-deepblue data-[selected=false]:border-grayblue transition-all duration-300 @container/expandable-tile group/expandable-tile cursor-pointer relative overflow-hidden ${className}`}
      data-selected={selected}
      ref={divRef}
      onClick={onInformationClick}
    >
      <LandThumbnail
        land={land}
        className='absolute z-0 top-0 left-0 w-full h-full border-0 object-cover opacity-30 @2xl/expandable-tile:hidden '
      />

      <div className='absolute z-0 top-0 w-full h-full left-0 border-0 bg-gradient-to-t from-black/80 @2xl/expandable-tile:hidden group-hover/expandable-tile:opacity-50 transition-all duration-300' />

      <div className='flex gap-5 items-stretch relative z-10 px-5 pb-5 overflow-hidden'>
        <div className='hidden relative min-w-[124px] rounded-xl overflow-hidden @2xl/expandable-tile:block cursor-pointer bg-red-500'>
          <LandThumbnail land={land} className='absolute z-0 w-full h-full object-cover ' />
          <div
            className={
              'absolute top-0 left-0 z-10 w-full h-full flex-center bg-gray-400/50 opacity-0 group-hover/expandable-tile:opacity-100 transition-all duration-300'
            }
          >
            <InformationCircleIcon className='w-8 h-8 fill-white stroke-transparent' />
          </div>
        </div>

        <div className='grow flex flex-col gap-2 overflow-hidden'>
          <div className='flex justify-between items-center gap-x-5 overflow-hidden'>
            <div className='whitespace-nowrap'>
              <span className='block text-sm @2xl/expandable-tile:text-xl text-white font-bold'>
                {t('components.portfolioLandAsset.info.title', {
                  landName: land.name,
                })}
              </span>
              <span className='block text-xs @2xl/expandable-tile:text-sm text-white font-bold'>
                {t('components.landNameHeader.subtitle', {
                  context: land.type === 'NEIGHBORHOOD' ? 'neighborhood' : 'city',
                  landCity: land.city,
                  landState: land.state,
                })}
              </span>
            </div>

            <div className='overflow-hidden'>
              <div className='w-fit flex justify-end gap-3 h-8'>
                {isCurrentUser && (
                  <WishlistButton
                    land={land}
                    landMarketInfo={landMarketInfo}
                    className='border-white h-full'
                    style={{
                      iconWidth: 'w-6',
                      starStrokeUnselected: 'stroke-white',
                      starStrokeUnselectedHover: false,
                    }}
                  />
                )}

                {isCurrentUser && (
                  <AsyncButton className='text-xs @2xl/expandable-tile:text-sm' size='xs' onClick={onTradeButtonClick}>
                    {t('actions.trade')}
                  </AsyncButton>
                )}

                {isCurrentUser && (
                  <AsyncButton
                    className='text-xs @2xl/expandable-tile:text-sm'
                    size='xs'
                    variant='hollow'
                    onClick={onRentButtonClick}
                  >
                    {t('actions.rent')}
                  </AsyncButton>
                )}

                <IconButton
                  onClick={onExpandButtonClick}
                  IconComponent={ChevronDownIcon}
                  className='aspect-square flex-center rounded-full bg-deepblue hover:opacity-70'
                  iconClassName={`w-5 h-5 fill-white stroke-white ${isOpen ? '-scale-100' : ''}`}
                />
              </div>
            </div>
          </div>

          <div className='flex flex-wrap gap-x-8 gap-y-2'>
            <Section className='@3xl/expandable-tile:max-w-[300px]'>
              <SectionTitle>{t('components.portfolioLandAsset.market.title')}</SectionTitle>
              <SectionContent>
                <TitleValue>
                  {t('components.portfolioLandAsset.market.sections.price')}
                  <LandMarketIndicator
                    className=''
                    land={land}
                    range='daily'
                    landMarketInfo={landMarketInfo}
                    iconSize='xs'
                    showDifference={false}
                  />
                </TitleValue>

                <TitleValue>
                  {t('components.portfolioLandAsset.market.sections.premium')}
                  {premiumRatio}
                </TitleValue>

                <TitleValue>
                  {t('components.portfolioLandAsset.market.sections.rentalYield')}
                  <RentalYieldValue land={land} />
                </TitleValue>
              </SectionContent>
            </Section>

            {(landAccountInfo.landTokenAmount > 0 ||
              landAccountInfo.borrowedLandTokenAmount > 0 ||
              landAccountInfo.lpTokenAmount > 0 ||
              portfolioValue.orders.length > 0) && (
              <Section className='@3xl/expandable-tile:max-w-[260px]'>
                <SectionTitle>{t('components.portfolioLandAsset.balance.title')}</SectionTitle>
                <SectionContent>
                  {landAccountInfo.landTokenAmount > 0 && (
                    <>
                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.owned')}
                        {t('utils.nativeAmount', {
                          amount: landAccountInfo.landTokenAmount,
                        })}
                      </TitleValue>

                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.value')}
                        {t('utils.stableTokenAmount', {
                          currencySymbol: displayPref.symbol,
                          amount: displayPref.displayAmount(landAccountInfo.landTokenMarketValue),
                        })}
                      </TitleValue>
                    </>
                  )}

                  {landAccountInfo.borrowedLandTokenAmount > 0 && (
                    <>
                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.borrowed')}
                        {t('utils.nativeAmount', {
                          amount: landAccountInfo.borrowedLandTokenAmount,
                        })}
                      </TitleValue>
                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.collateral')}
                        <DotPercentageCollateralPositionIndicator percentage={landAccountInfo.collateralRatio / 100} />
                      </TitleValue>
                    </>
                  )}

                  {landAccountInfo.lpTokenAmount > 0 && (
                    <>
                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.renting')}
                        {t('utils.nativeAmount', {
                          amount: liquidityReceivedForLiquidityProviderToken(
                            landAccountInfo.lpTokenAmount,
                            landMarketInfo,
                          ).landTokenAmount,
                        })}
                      </TitleValue>
                    </>
                  )}

                  {portfolioValue.orders.length > 0 && (
                    <>
                      <TitleValue>
                        {t('components.portfolioLandAsset.balance.sections.orders')}
                        {t('utils.number', {
                          value: portfolioValue.orders.length,
                        })}
                      </TitleValue>
                    </>
                  )}
                </SectionContent>
              </Section>
            )}
          </div>
        </div>
      </div>
      {isOpen && (
        <div className='flex flex-wrap gap-5 bg-gradient-to-t from-graybluedark3 via-graybluedark3 rounded-xl px-5 pb-5 relative z-10'>
          <Section className='max-w-min'>
            <SectionTitle>{t('components.portfolioLandAsset.renting.title')}</SectionTitle>
            <SectionContent className='@2xl/expandable-tile:max-w-min'>
              <TitleValue>
                {t('components.portfolioLandAsset.renting.sections.tokens')}
                {t('utils.nativeTokenAmount', {
                  currencySymbol: landMarketInfo.lpSymbol,
                  amount: landAccountInfo.lpTokenAmount,
                })}
              </TitleValue>
              <TitleValue>
                {t('components.portfolioLandAsset.renting.sections.value')}
                {t('utils.stableTokenAmount', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(
                    liquidityReceivedForLiquidityProviderToken(landAccountInfo.lpTokenAmount, landMarketInfo)
                      .stableTokenAmount,
                  ),
                })}
              </TitleValue>
            </SectionContent>
          </Section>

          <Section className='max-w-min'>
            <SectionTitle>{t('components.portfolioLandAsset.rewards.title')}</SectionTitle>
            <SectionContent className='@2xl/expandable-tile:max-w-min'>
              <TitleValue>
                {t('components.portfolioLandAsset.rewards.sections.claimableAmount')}
                {t('utils.stableTokenAmount', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(landAccountInfo.claimableStableToken),
                })}
              </TitleValue>
              {isCurrentUser && (
                <AsyncButton
                  className='text-xs @2xl/expandable-tile:text-sm mt-1 h-8'
                  size='xs'
                  variant='hollow'
                  disabled={landAccountInfo.claimableStableToken === 0}
                  onClick={onClaimButtonClick}
                >
                  {t('components.portfolioLandAsset.rewards.sections.claimCta')}
                </AsyncButton>
              )}
            </SectionContent>
          </Section>
        </div>
      )}
    </div>
  );
};

const RentalYieldValue: FunctionComponent<{land: Land}> = ({land}) => {
  const {data, isLoading, error} = useLandRentalYield(land.tokenSymbol);
  const {t} = useTranslation('translation');

  if (isLoading) {
    return <LoadingSpinner className={'w-4 h-4 text-deepblue fill-graybluedark2 my-0.5'} />;
  }

  if (error) {
    <span className='text-downred italic'>{t('pages.actionMenu.components.contentSection.infoRow.errorLoading')}</span>;
  }

  if (data === undefined || data === null) {
    return <span>{t('pages.actionMenu.components.contentSection.infoRow.empty')}</span>;
  }

  return <>{t('utils.percentage', {value: data})}</>;
};

const Section: FunctionComponent<
  {
    children: ReactNode;
  } & ClassName
> = ({children, className}) => <div className={`space-y-2 grow ${className}`}>{children}</div>;

const SectionTitle: FunctionComponent<
  {
    children: ReactNode;
  } & ClassName
> = ({children}) => <span className={'text-xs text-deepblue font-bold uppercase'}>{children}</span>;

const SectionContent: FunctionComponent<
  {
    children: ReactNode[];
  } & ClassName
> = ({children, className}) => <div className={`flex flex-col gap-0.5 items-start ${className}`}>{children}</div>;

const TitleValue: FunctionComponent<{
  children: [ReactNode, ReactNode];
}> = ({children}) => (
  <div className='w-full flex items-center gap-8 justify-between'>
    <div className='text-gray-400 text-xs font-bold whitespace-nowrap'>{children[0]}</div>
    <span className='text-sm font-bold text-white whitespace-nowrap truncate'>{children[1]}</span>
  </div>
);
